/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import { Card, Divider, Tab, Tabs, useMediaQuery } from "@material-ui/core";

import {
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { isSubscriptionDisabled } from "../../../utils/appUtils";

import { ChevronRightIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { Empty } from "../../../components/common/Empty";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { UserStatusIcon } from "../users/UserStatusIcon";
import { CarrierImportButton } from "./CarrierImportButton";

const tabs = [
  { id: "active", name: "Active", active: "true" },
  { id: "archived", name: "Archived", active: "false" },
];

const getSessionTab = () => {
  const sessionTab = Number(sessionStorage.getItem("CarriersTab"));
  if (sessionTab && tabs[sessionTab]) return sessionTab;
  return 0;
};

const CarrierSearchField = ({ label, alwaysOn, ...props }) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    debugger;
    if (value) {
      delete filterValues.or;
      const filters = {
        ...filterValues,
        or: [
          { businessName: { matchPhrasePrefix: `${value}` } },
          { businessName: { wildcard: `*${value}*` } },
          { email: { matchPhrasePrefix: `${value}` } },
          { email: { wildcard: `*${value}*` } },
        ],
      };

      setFilters(filters, displayedFilters);
    } else {
      delete filterValues.or;
      setFilters(filterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = () => [<CarrierSearchField label="Search" alwaysOn />];

const ListActions = ({ basePath, xsDown, ...props }) => {
  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      <CreateButton basePath={basePath} disabled={isSubscriptionDisabled()} />
      <CarrierImportButton disabled={isSubscriptionDisabled()} />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

export const CarrierList = (props) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [tabIndex, setTabIndex] = useState(getSessionTab());

  const [defaultFilter, setDefaultFilter] = useState({
    reloadYardId: window.appUser?.reloadYardId,
    activeBusinessName: { beginsWith: { active: tabs[tabIndex].active } },
  });

  const [defaultSearchFilter, setDefaultSearchFilter] = useState({
    reloadYardId: { eq: window.appUser?.reloadYardId },
    active: { eq: tabs[tabIndex].active },
  });

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"carrierByActiveByName", "search":"businessName"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10
  );

  const listContext = useSearchableList({
    ...props,
    resource: "carriers",
    basePath: "/carriers",
    defaultTitle: "Carriers",
    defaultFilter: defaultFilter,
    defaultSort: defaultSort,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  const handleTabChange = (value) => {
    setTabIndex(value);
    setDefaultFilter({
      reloadYardId: window.appUser?.reloadYardId,
      activeBusinessName: { beginsWith: { active: tabs[value].active } },
    });
    setDefaultSearchFilter({
      reloadYardId: { eq: window.appUser?.reloadYardId },
      active: { eq: tabs[value].active },
    });
    listContext.setPage(1);
  };

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Carriers" />} />
      <ListToolbar
        {...props}
        filters={getListFilters()}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card>
        <CarrierTabs
          {...props}
          {...listContext}
          tabValue={tabIndex}
          setTabValue={handleTabChange}
          xsDown={xsDown}
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const CarrierTabs = ({ tabValue, setTabValue, ...props }) => {
  const { xsDown } = props;

  const handleChange = (event, value) => {
    sessionStorage.setItem("CarriersTab", value);
    setTabValue(value);
  };

  return (
    <>
      <Tabs
        value={tabValue}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
      >
        {tabs.map((itm, idx) => (
          <Tab key={itm.id} label={itm.name} id={`carrier-${idx}`} />
        ))}
      </Tabs>
      <Divider />
      {tabs.map(
        (itm) =>
          itm.id === tabs[tabValue].id && (
            <CarrierGrid {...props} key={itm.id} xsDown={xsDown} />
          )
      )}
    </>
  );
};

const CarrierGrid = (props) => {
  const { xsDown } = props;
  const { ids, page } = useListContext();

  if (ids?.length === 0 && page === 1)
    return <Empty {...props} message="Create a new carrier" />;
  return (
    <>
      {xsDown ? (
        <SimpleList
          primaryText={(record) => record.businessName}
          secondaryText={(record) => record.email}
          tertiaryText={(record) => record.phone}
          linkType="edit"
          leftIcon={(record) => <UserStatusIcon record={record} />}
          rightIcon={() => (
            <ChevronRightIcon
              fontSize="large"
              color="secondary"
              style={{ marginLeft: 40, marginTop: 10 }}
            />
          )}
        />
      ) : (
        <Datagrid rowClick="edit">
          <TextField
            source="businessName"
            label="Carrier"
            sortable={true}
            sortBy={`{"key":"carrierByActiveByName", "search":"businessName"}`}
          />
          <TextField source="email" label="Email" sortable={false} />
          <TextField source="phone" label="Phone" sortable={false} />
          <EditButton />
        </Datagrid>
      )}
    </>
  );
};
